import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';

import { useTimetablesQuery } from './useTimetables.query';

const BASE_PATH = '/api/v5/daily_timetables';

export const DAILY_TIMETABLE_QUERY_KEY = 'dailyTimetable';
export const dailyTimetableQueryKey = (date: string) => [DAILY_TIMETABLE_QUERY_KEY, date] as const;
const REPLACE_MUTATION_KEY = [DAILY_TIMETABLE_QUERY_KEY, 'replace'] as const;

export type DailyTimetableEntity = {
  planningDate: string;
  timetableId: number | null;
  updatedAt: string | null;
};

type DailyTimetableResponse = {
  planning_date: string;
  timetable_id: number | null;
  updated_at: string | null;
};

const deserialize = (data: DailyTimetableResponse): DailyTimetableEntity => ({
  planningDate: data.planning_date,
  timetableId: data.timetable_id,
  updatedAt: data.updated_at,
});

type ReplaceVariables = { date: string; timetableId: number };
type ReplaceContext = { previous: DailyTimetableEntity | undefined };

const isReplacingDate = (queryClient: QueryClient, date: string) => queryClient.isMutating({
  mutationKey: REPLACE_MUTATION_KEY,
  predicate: (mutation) => (mutation.state.variables as ReplaceVariables | undefined)?.date === date,
}) > 0;

/**
 * 同じ日の選び直し（PUT）が全部終わるまで待つ。反映 API（その日のダイヤを同じ transaction で書く）の前に呼ぶ。
 * Why not 待たずに送る: 新規保存の後の PUT が反映より遅れて届くと、反映が書いた値を古い選択で上書きする
 */
export const waitForDailyTimetableReplaces = (queryClient: QueryClient, date: string): Promise<void> => {
  if (!isReplacingDate(queryClient, date)) return Promise.resolve();
  return new Promise((resolve) => {
    const unsubscribe = queryClient.getMutationCache().subscribe(() => {
      if (isReplacingDate(queryClient, date)) return;
      unsubscribe();
      resolve();
    });
  });
};

/**
 * 選択中のダイヤ ID を日付単位で DB に持つ hook（log_web_issues#361・設計書 C4）。
 * localStorage からの移行は持たない（固定ルートの ID はダイヤの ID にならない）。
 * カード配車への差し替えは #365。
 *
 * - `id`: その日に選ばれているダイヤ ID。一覧の取得が落ち着いていて一覧に無い ID は null として返す
 * - `setId(X)`: PUT で上書き（選び直し）。楽観更新し、失敗したら知らせる。その日の最後の PUT が終わったら取り直して DB の値に合わせる
 * - `setId(null)`: サーバは呼ばない。キャッシュを null にして取り直す
 * - `isResolved`: GET が返った（失敗も含む）。中央ボードの取得はこれを待つ
 */
export function useSelectedTimetableId(date: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const dailyQuery = useQuery<DailyTimetableEntity, Error>({
    queryKey: dailyTimetableQueryKey(date),
    queryFn: async () => {
      const res = await axios.get<DailyTimetableResponse>(`${BASE_PATH}/${date}`);
      return deserialize(res.data);
    },
    enabled: Boolean(date),
    // Why not 既定の retry: API が未デプロイの 404 を 3 回（約 7 秒）待っても結果は変わらない。
    // ネットワーク断などは既定どおり再試行する
    retry: (failureCount, error) => !(axios.isAxiosError(error) && error.response?.status === 404) && failureCount < 3,
  });

  const timetablesQuery = useTimetablesQuery();

  // ---- 選び直し（PUT）----
  // 同じ日の PUT がまだ残っているか。onError / onSettled の中では自分も pending として数える（query-core は
  // コールバックの後に status を変える）ので、1 なら自分が最後
  const isLastReplace = useCallback((targetDate: string) => queryClient.isMutating({
    mutationKey: REPLACE_MUTATION_KEY,
    predicate: (mutation) => (mutation.state.variables as ReplaceVariables | undefined)?.date === targetDate,
  }) === 1, [queryClient]);

  const replaceMutation = useMutation<DailyTimetableEntity, Error, ReplaceVariables, ReplaceContext>({
    mutationKey: REPLACE_MUTATION_KEY,
    mutationFn: async ({ date: targetDate, timetableId }) => {
      const res = await axios.put<DailyTimetableResponse>(`${BASE_PATH}/${targetDate}`, { data: { timetable_id: timetableId } });
      return deserialize(res.data);
    },
    onMutate: async ({ date: targetDate, timetableId }) => {
      const key = dailyTimetableQueryKey(targetDate);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<DailyTimetableEntity>(key);
      queryClient.setQueryData<DailyTimetableEntity>(key, {
        planningDate: targetDate, timetableId, updatedAt: previous?.updatedAt ?? null,
      });
      return { previous };
    },
    // Why not onSuccess で応答を書き込む: 続けて選び直すと応答の届く順が DB に入った順と逆になりうる
    // （DB は 7 なのに、後から届いた 5 の応答で取り直しが返るまで 5 を見せる）。表示は楽観値のまま、最後の PUT の後に取り直す
    onError: (_error, { date: targetDate }, context) => {
      enqueueSnackbar('ダイヤの選択を保存できませんでした', { variant: 'error' });
      // Why not 残りの PUT があっても戻す: 後の選び直しの楽観値（いま見せたい値）を消してしまう
      if (!isLastReplace(targetDate)) return;
      // 戻した値は表示を一瞬で戻すためだけに使い、DB の値には onSettled の取り直しで合わせる。
      // 続けて選び直していれば previous は前の楽観値で、DB の値ではない
      if (context?.previous) queryClient.setQueryData(dailyTimetableQueryKey(targetDate), context.previous);
    },
    onSettled: (_data, _error, { date: targetDate }) => {
      // Why not 毎回取り直す: 残りの PUT の前に読んだ古い値が、その PUT の楽観値や結果を上書きする。
      // invalidateQueries は実行中の GET を取り消してから取り直す（cancelRefetch の既定は true）
      if (!isLastReplace(targetDate)) return;
      void queryClient.invalidateQueries({ queryKey: dailyTimetableQueryKey(targetDate) });
    },
  });
  const { mutate: replace } = replaceMutation;

  const setId = useCallback((next: number | null) => {
    if (!date) return;
    if (next == null) {
      const key = dailyTimetableQueryKey(date);
      queryClient.setQueryData<DailyTimetableEntity>(key, { planningDate: date, timetableId: null, updatedAt: null });
      void queryClient.invalidateQueries({ queryKey: key });
      return;
    }
    replace({ date, timetableId: next });
  }, [date, queryClient, replace]);

  // ---- 派生 ----
  const dbId = dailyQuery.data?.timetableId ?? null;
  const timetables = timetablesQuery.data;
  const isListSettled = timetablesQuery.isSuccess && !timetablesQuery.isFetching;
  const id = useMemo(() => {
    if (dbId == null) return null;
    // 一覧の再取得中（新規保存直後の invalidate など）は DB の値をそのまま返す。
    // Why not 常に一覧で絞る: 新規保存直後は古い一覧に新しい ID が無く、選択が一瞬外れる
    if (isListSettled && timetables && !timetables.some((t) => t.id === dbId)) return null;
    return dbId;
  }, [dbId, isListSettled, timetables]);

  const isResolved = !date || dailyQuery.isError || dailyQuery.isSuccess;

  return [id, setId, { isResolved }] as const;
}
